import { ReactNode, forwardRef } from "react";

import { InputBaseProps } from "capitalrx-components-2.0/lib/forms/InputBaseProps";
import { RefComponent } from "capitalrx-components-2.0/lib/forms/RefComponent";
import { Placeholder } from "capitalrx-components-2.0/lib/forms/Textbox/Placeholder";
import { withSubComponents } from "capitalrx-components-2.0/lib/utilities/withSubComponents";
import { CountryCode } from "libphonenumber-js";

import { Field } from "./Field";
import { Input } from "./Input";

/**
 * The `TelephoneTextbox` component properties.
 */
export interface TelephoneTextboxProps extends Omit<InputBaseProps<string | null>, "style"> {
  /**
   * Optional ISO 3166-1 alpha-2 country code (e.g., "US", "GB").
   * When provided, input will format/validate as a national number for that country.
   * When omitted, input will behave as international and prefer E.164 formatting.
   */
  readonly countryCode?: CountryCode;

  /**
   * Optional list of countries to render a selector. If provided, a selector will be shown next to the input.
   */
  readonly countryOptions?: Array<{
    code: CountryCode;
    label: ReactNode;
  }>;

  /**
   * Called when the user selects a country from the selector.
   */
  readonly onCountryChange?: (code: CountryCode) => void;
}

/**
 * The `TelephoneTextbox` component sub components.
 */
export interface TelephoneTextboxSubComponents {
  /**
   * A React Hook Form compatible TelephoneTextbox Field component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * A placeholder component for the TelephoneTextbox which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

/**
 * An input component which allows the user to enter a Telephone.
 */
export const TelephoneTextbox = withSubComponents<RefComponent<HTMLInputElement, TelephoneTextboxProps>, TelephoneTextboxSubComponents>(
  {
    Field,
    Placeholder,
  },
  forwardRef<HTMLInputElement, TelephoneTextboxProps>(function TelephoneTextbox(props, ref) {
    return <Input {...props} ref={ref} />;
  }),
);
